/* istanbul ignore file */
import { CommonModule } from '@angular/common';
import { Component, InputSignal, Signal, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TravelType } from '@app/enums/travel-type.enum';
import { AlertComponent } from '@components/atoms/alert/alert.component';
import { MedpaceDatepickerComponent } from '@components/atoms/medpace-datepicker/medpace-datepicker.component';
import { MedpaceTextareaAutocompleteComponent } from '@components/atoms/textarea-autocomplete/medpace-textarea-autocomplete.component';
import {
    MdsAutocompleteComponent,
    MdsDatepickerComponent,
    MdsDropdownComponent,
    MdsRadioButton,
    MdsRadioGroupComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import { MedpaceAutocompleteObject } from '@models/medpaceAutocompleteObject';
import { TravelViewModelService } from '@services/travel-view-model/travel-view-model.service';
import { PersistentFormControl } from '@utility/persistent-forms';
import { MdsOptionGeneric, getUniqueObjects, getUniqueStrings } from '@utility/utility';
import { Moment, utc } from 'moment';
import { combineLatest, filter, map, shareReplay, switchMap, tap } from 'rxjs';
import { RentalCarRequestDetailsFormGroup } from '../../../../form-groups/detail-form-groups/travel/rental-car-request.formgroup';

@Component({
    selector: 'medpace-car-rental-request-details-edit',
    templateUrl: './car-rental-request-details-edit.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        AlertComponent,
        MdsAutocompleteComponent,
        MdsRadioGroupComponent,
        MedpaceDatepickerComponent,
        MdsDropdownComponent,
        MedpaceTextareaAutocompleteComponent,
        MdsDatepickerComponent,
    ],
})
export class CarRentalRequestDetailsEditComponent {
    TravelType = TravelType;
    private readonly travelViewModelService = inject(TravelViewModelService);

    public readonly formGroup: InputSignal<RentalCarRequestDetailsFormGroup> = input.required();
    private readonly formGroup$ = toObservable(this.formGroup);
    private readonly travelFormGroup$ = toObservable(this.travelViewModelService.travelFormGroup);
    protected readonly needsFlightTravel$ = this.travelFormGroup$.pipe(
        switchMap((travelFormGroup) =>
            travelFormGroup.controls.travelDetails.controls.flight.controls.needsTravel.valueChangesStartWithCurrentValue()
        ),
        shareReplay(1)
    );
    protected readonly flightDetails$ = this.travelFormGroup$.pipe(
        switchMap((travelFormGroup) =>
            travelFormGroup.controls.travelDetails.controls.flight.controls.travel.controls.requestDetails.controls.patientFlightDetails.valueChangesStartWithCurrentValue()
        ),
        shareReplay(1)
    );
    protected readonly useFlightDate$ = this.formGroup$.pipe(
        switchMap((formGroup) => formGroup.controls.useFlightDate.valueChangesStartWithCurrentValue()),
        shareReplay(1)
    );
    protected readonly useFlightDetails$ = combineLatest({
        useFlightDate: this.useFlightDate$,
        needsFlightTravel: this.needsFlightTravel$,
    }).pipe(
        map(({ needsFlightTravel, useFlightDate }) => {
            return needsFlightTravel && useFlightDate;
        }),
        shareReplay(1)
    );
    protected readonly enableUseFlightDetails$ = combineLatest({
        formGroup: this.formGroup$,
        needsFlightTravel: this.needsFlightTravel$,
    })
        .pipe(
            tap(({ needsFlightTravel, formGroup }) => {
                if (needsFlightTravel) {
                    formGroup.controls.useFlightDate.enable({ breakPersistence: true });
                } else {
                    formGroup.controls.useFlightDate.disable({ persistent: true });
                }
            }),
            takeUntilDestroyed()
        )
        .subscribe();

    protected readonly enableCarRentalDetails$ = combineLatest({
        formGroup: this.formGroup$,
        useFlightDetails: this.useFlightDetails$,
    })
        .pipe(
            tap(({ formGroup, useFlightDetails }) => {
                if (!useFlightDetails) {
                    formGroup.controls.pickupAddress.enable({ breakPersistence: true });
                    formGroup.controls.pickupCountry.enable({ breakPersistence: true });
                    formGroup.controls.pickupDate.enable({ breakPersistence: true });
                    formGroup.controls.pickupTime.enable({ breakPersistence: true });
                    formGroup.controls.dropOffDate.enable({ breakPersistence: true });
                    formGroup.controls.dropOffTime.enable({ breakPersistence: true });
                } else {
                    formGroup.controls.pickupAddress.disable({ persistent: true });
                    formGroup.controls.pickupCountry.disable({ persistent: true });
                    formGroup.controls.pickupDate.disable({ persistent: true });
                    formGroup.controls.pickupTime.disable({ persistent: true });
                    formGroup.controls.dropOffDate.disable({ persistent: true });
                    formGroup.controls.dropOffTime.disable({ persistent: true });
                }
            }),
            takeUntilDestroyed()
        )
        .subscribe();

    airportDestination = new PersistentFormControl<string | null>({ value: null, disabled: true }); // dummy controls, needed to copy value from flight controls and to show MDS textfields as disabled
    departureDate = new PersistentFormControl<Date | null>({ value: null, disabled: true });
    returnDate = new PersistentFormControl<Date | null>({ value: null, disabled: true });
    flightTravelType$ = this.travelFormGroup$.pipe(
        switchMap((travelFormGroup) =>
            travelFormGroup.controls.travelDetails.controls.flight.controls.travel.controls.requestDetails.controls.patientFlightDetails.controls.travelType.valueChangesStartWithCurrentValue()
        ),
        shareReplay(1)
    );
    patchAirportDestination$ = combineLatest({
        airportDestination: this.travelFormGroup$.pipe(
            switchMap((travelFormGroup) =>
                travelFormGroup.controls.travelDetails.controls.flight.controls.travel.controls.requestDetails.controls.patientFlightDetails.controls.airportDestination.valueChangesStartWithCurrentValue()
            )
        ),
        useFlightDetails: this.useFlightDetails$,
    })
        .pipe(
            filter(({ useFlightDetails }) => useFlightDetails),
            tap(({ airportDestination }) => {
                this.airportDestination.setValue(airportDestination);
            }),
            takeUntilDestroyed()
        )

        .subscribe();
    patchDepartureDate$ = combineLatest({
        departureDate: this.travelFormGroup$.pipe(
            switchMap((travelFormGroup) =>
                travelFormGroup.controls.travelDetails.controls.flight.controls.travel.controls.requestDetails.controls.patientFlightDetails.controls.departureDate.valueChangesStartWithCurrentValue()
            )
        ),
        useFlightDetails: this.useFlightDetails$,
    })
        .pipe(
            filter(({ useFlightDetails }) => useFlightDetails),
            tap(({ departureDate }) => {
                if (departureDate) this.departureDate.setValue(departureDate.toDate());
            }),
            takeUntilDestroyed()
        )
        .subscribe();
    patchReturnDate$ = combineLatest({
        returnDate: this.travelFormGroup$.pipe(
            switchMap((travelFormGroup) =>
                travelFormGroup.controls.travelDetails.controls.flight.controls.travel.controls.requestDetails.controls.patientFlightDetails.controls.returnDate.valueChangesStartWithCurrentValue()
            )
        ),
        useFlightDetails: this.useFlightDetails$,
    })
        .pipe(
            filter(({ useFlightDetails }) => useFlightDetails),
            tap(({ returnDate }) => {
                if (returnDate) this.returnDate.setValue(returnDate.toDate());
            }),
            takeUntilDestroyed()
        )
        .subscribe();

    public readonly timeOptions: Signal<MdsOptionGeneric<number>[]> = computed(
        () => this.travelViewModelService.travelCardViewModel().timeOptions ?? []
    );

    public readonly countryOptions: Signal<string[]> = computed(
        () => this.travelViewModelService.travelCardViewModel().countryOptions?.map((s) => s.viewValue) ?? []
    );

    public readonly autocompleteOptions: Signal<{
        pickupAddress: string[];
        specialRequests: MedpaceAutocompleteObject[];
    }> = computed(() => {
        const history = this.travelViewModelService.travelCardViewModel().historyData.carRental;

        return {
            pickupAddress: getUniqueStrings(
                history.filter((data) => !!data.pickUpLocation).map((data) => data.pickUpLocation)
            ),
            specialRequests: getUniqueObjects(
                history
                    .filter((data) => !!data.specialRequest)
                    .map(
                        (data) =>
                            <MedpaceAutocompleteObject>{ requestDate: data.requestDate, value: data.specialRequest }
                    )
            ),
        };
    });

    protected useFlightDateRadioButtons: MdsRadioButton[] = [
        {
            label: 'Rental car pick-up/drop-off details are the same as for flight',
            value: true,
            name: 'useFlightDate_True',
            id: 'useFlightDate_True',
        },
        {
            label: 'Customize rental car pick-up and drop-off details',
            value: false,
            name: 'useFlightDate_False',
            id: 'useFlightDate_False',
        },
    ];

    public getDateFromMinutesSinceMidnight(minutesSinceMidnight: number): Moment {
        return utc().startOf('day').add(minutesSinceMidnight, 'minutes');
    }
}
